// V4.14 — public read-only AI analysis (share-link receiver view).
// Renders the server snapshot: AI verdict + canvas widgets (listings,
// compare, map, price distribution) exactly as generated. Mobile-first,
// no login, no private data — plus a conversion CTA + subtle footer.
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Bot, Sparkles } from "lucide-react";
import { WIDGET_RENDERERS } from "../ai/ChatWidgets";

function renderBold(text) {
  const parts = String(text || "").split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold text-zinc-50">{p}</strong>
    ) : (
      p
    ),
  );
}

export const PublicAnalysisView = ({ data, shareId }) => {
  const { t } = useTranslation("share");
  const registerHref = `/register?${new URLSearchParams({
    ...(data.referral_code ? { ref: data.referral_code } : {}),
    share: shareId,
  }).toString()}`;
  const created = data.created_at
    ? new Date(data.created_at).toLocaleDateString("de-AT")
    : null;

  return (
    <article className="space-y-5" data-testid="public-analysis-view">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-amber-400">
          <Bot className="h-4 w-4" />
          {t("analysis.badge")}
        </div>
        {created && (
          <span className="text-[11px] text-zinc-500">
            {t("analysis.createdAt", { date: created })}
          </span>
        )}
      </div>

      <h1
        className="text-2xl font-semibold text-zinc-100"
        data-testid="public-analysis-title"
      >
        {data.title}
      </h1>

      {data.inactive_count > 0 && (
        <div
          data-testid="public-analysis-inactive-note"
          className="flex items-start gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {t("analysis.inactiveNote")}
        </div>
      )}

      {data.assistant_message && (
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-emerald-300">
            <Sparkles className="h-3.5 w-3.5" />
            {t("analysis.aiTitle")}
          </div>
          <p
            className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-zinc-200"
            data-testid="public-analysis-message"
          >
            {renderBold(data.assistant_message)}
          </p>
        </div>
      )}

      {(data.widgets || []).map((w, i) => {
        const R = WIDGET_RENDERERS[w.type];
        if (!R) return null;
        return (
          <div key={`${w.type}-${i}`} data-testid={`public-analysis-widget-${w.type}`}>
            <R widget={w} variant="public" />
          </div>
        );
      })}

      <div
        className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.07] p-5 text-center"
        data-testid="public-analysis-cta"
      >
        <div className="text-base font-semibold text-zinc-100">
          {t("analysis.likeItTitle")}
        </div>
        <p className="mt-1 text-sm text-zinc-400">{t("analysis.likeItBody")}</p>
        <Link
          to={registerHref}
          data-testid="public-analysis-register"
          className="mt-4 inline-block rounded-full bg-amber-500 px-6 py-2.5 text-sm font-semibold text-zinc-950 transition-colors hover:bg-amber-400"
        >
          {t("analysis.likeItBtn")}
        </Link>
      </div>

      <p
        className="pb-4 text-center text-[11px] text-zinc-600"
        data-testid="public-analysis-footer"
      >
        {t("analysis.footer")} —{" "}
        <Link
          to={registerHref}
          className="text-zinc-500 underline-offset-2 transition-colors hover:text-amber-300 hover:underline"
        >
          {t("analysis.footerCta")}
        </Link>
      </p>
    </article>
  );
};
